import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, exhaustMap, map, switchMap, switchMapTo, withLatestFrom } from 'rxjs/operators';
import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { AddBlockedPhoneNumberComponent } from '../../add-blocked-phone-number/containers/add-blocked-phone-number.component';
import { BlockedPhoneNumberFilterOptions } from '../models/blocked-phone-number-filter-option';
import { BlockedPhoneNumberRegisterOptions } from '../models/blocked-phone-number-register-options';
import {
   BlockedPhoneNumberRegisterListItem,
   BlockedPhoneNumberRegisterResults,
} from '../models/blocked-phone-number-register-results.model';
import {
   GetBlockedPhoneNumberRegister,
   GetBlockedPhoneNumberRegister_blockedPhoneNumbersConnection_items,
} from '../queries/generated/GetBlockedPhoneNumberRegister';
import { BlockedPhoneNumberRegisterApiService } from '../services/blocked-phone-number-register-api.service';
import * as BlockedPhoneNumberRegisterActions from './blocked-phone-number-register.actions';
import {
   blockedPhoneNumberRegisterAdapterActions,
   createBlockedPhoneNumber,
} from './blocked-phone-number-register.actions';
import {
   BlockedPhoneNumberRegisterState,
   selectEndCursor,
   selectQuickFilters,
} from './blocked-phone-number-register.reducer';
import * as BlockedPhoneNumberRegisterSelectors from './blocked-phone-number-register.selectors';
import { selectFilters } from './blocked-phone-number-register.selectors';

@Injectable()
export class BlockedPhoneNumberRegisterEffects {
   addBlockedPhoneNumber$ = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(createBlockedPhoneNumber),
            exhaustMap(() => {
               const dialogRef = this.dialogService.openFormComponent(AddBlockedPhoneNumberComponent, {
                  disableClose: true,
                  panelClass: ['add-dialog-panel', 'dialog-container'],
               });
               this.dialogId = dialogRef.id;
               return dialogRef.afterClosed().pipe(switchMapTo(EMPTY));
            })
         );
      },
      { dispatch: false }
   );

   loadBlockedPhoneNumberList$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(blockedPhoneNumberRegisterAdapterActions.pageEnter, BlockedPhoneNumberRegisterActions.refreshList),
         withLatestFrom(this.store.pipe(select(selectFilters)), this.store.pipe(select(selectQuickFilters))),
         switchMap(([, filters, quickFilters]) => {
            return this.loadNewData(filters, quickFilters);
         })
      );
   });

   loadBlockedPhoneNumberPage$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(blockedPhoneNumberRegisterAdapterActions.loadPage),
         withLatestFrom(
            this.store.pipe(select(BlockedPhoneNumberRegisterSelectors.selectFilters)),
            this.store.pipe(select(selectEndCursor)),
            this.store.pipe(select(selectQuickFilters))
         ),
         switchMap(([action, filters, endCursor, quickFilters]) => {
            return this.loadNewPage(endCursor, filters, quickFilters);
         })
      );
   });

   quickFilterChanged$ = createEffect(() => {
      return this.actions$.pipe(
         ofType(blockedPhoneNumberRegisterAdapterActions.quickFilterChanged),
         withLatestFrom(
            this.store.pipe(select(selectQuickFilters)),
            this.store.pipe(select(BlockedPhoneNumberRegisterSelectors.selectFilters))
         ),
         switchMap(([action, quickFilters, filters]) => {
            return this.loadNewData(filters, quickFilters);
         })
      );
   });

   deleteBlockedPhoneNumber = createEffect(() => {
      return this.actions$.pipe(
         ofType(BlockedPhoneNumberRegisterActions.deleteBlockedPhoneNumber),
         switchMap((action) => {
            const message = 'Are you sure you would like to unblock this Phone Number?';

            const dialogRef = this.confirmationService.confirm({
               data: {
                  header: 'Unblock Phone Number?',
                  message,
               },
               size: DialogSize.Medium,
            });

            return dialogRef.afterClosed().pipe(
               switchMap((confirmed) => {
                  if (confirmed) {
                     return this.blockedPhoneNumberRegisterApiService
                        .deleteBlockedPhoneNumber(action.blockedPhoneNumberId)
                        .pipe(
                           map(() => {
                              return BlockedPhoneNumberRegisterActions.deleteBlockedPhoneNumberSuccess({
                                 phoneNumber: action.phoneNumber,
                              });
                           }),
                           catchError((error) => {
                              return of(BlockedPhoneNumberRegisterActions.deleteBlockedPhoneNumberFailure(error));
                           })
                        );
                  }
                  return EMPTY;
               })
            );
         })
      );
   });

   deleteBlockedPhoneNumberSuccess = createEffect(() => {
      return this.actions$.pipe(
         ofType(BlockedPhoneNumberRegisterActions.deleteBlockedPhoneNumberSuccess),
         switchMap((action) => {
            this.dialogService.closeFormComponent();
            this.snackBar.open(`Phone Number ${action.phoneNumber} Unblocked`);
            return of(BlockedPhoneNumberRegisterActions.refreshList());
         })
      );
   });

   dialogId: string;
   constructor(
      private actions$: Actions,
      private confirmationService: ConfirmationDialogService,
      private store: Store<BlockedPhoneNumberRegisterState>,
      private blockedPhoneNumberRegisterApiService: BlockedPhoneNumberRegisterApiService,
      private snackBar: MatSnackBar,
      private router: Router,
      private dialogService: LcsDialogService
   ) {}

   private loadNewData(
      filters: BlockedPhoneNumberFilterOptions[],
      quickFilters: BlockedPhoneNumberRegisterOptions | null = null
   ) {
      return this.blockedPhoneNumberRegisterApiService.getBlockedPhoneNumbers(null, filters, quickFilters, null).pipe(
         map((result) => {
            return blockedPhoneNumberRegisterAdapterActions.loadSuccess(
               this.convertGetBlockedPhoneNumberResultToLoadSuccessProps(result.data)
            );
         }),
         catchError((error) => {
            return of(blockedPhoneNumberRegisterAdapterActions.loadFailure(error));
         })
      );
   }

   private loadNewPage(
      endCursor: string | null,
      filters: BlockedPhoneNumberFilterOptions[],
      quickFilters: BlockedPhoneNumberRegisterOptions | null = null
   ) {
      return this.blockedPhoneNumberRegisterApiService
         .getBlockedPhoneNumbers(endCursor, filters, quickFilters, null)
         .pipe(
            map((result) => {
               return blockedPhoneNumberRegisterAdapterActions.loadPageSuccess(
                  this.convertGetBlockedPhoneNumberResultToLoadSuccessProps(result.data)
               );
            }),
            catchError((error) => {
               return of(blockedPhoneNumberRegisterAdapterActions.loadPageFailure(error));
            })
         );
   }

   private convertGetBlockedPhoneNumberResultToLoadSuccessProps(
      getBlockedPhoneNumber: GetBlockedPhoneNumberRegister
   ): BlockedPhoneNumberRegisterResults {
      return {
         endCursor: getBlockedPhoneNumber.blockedPhoneNumbersConnection.pageInfo.endCursor,
         hasNextPage: getBlockedPhoneNumber.blockedPhoneNumbersConnection.pageInfo.hasNextPage,
         items: getBlockedPhoneNumber.blockedPhoneNumbersConnection.items.map(
            this.convertBlockedPhoneNumberRegisterListItem
         ),
         totalCount: getBlockedPhoneNumber.blockedPhoneNumbersConnection.totalCount,
      };
   }

   private convertBlockedPhoneNumberRegisterListItem(
      getBlockedPhoneNumberItem: GetBlockedPhoneNumberRegister_blockedPhoneNumbersConnection_items
   ): BlockedPhoneNumberRegisterListItem {
      return {
         phoneNumber: getBlockedPhoneNumberItem?.phoneNumber ?? null,
         blockedPhoneNumberId: getBlockedPhoneNumberItem?.blockedPhoneNumberId ?? null,
         comment: getBlockedPhoneNumberItem?.comment ?? null,
      };
   }
}
